import { registerAttachment, attachmentMarkdown, uploadEncrypted } from '../data/attachments.ts';
import { createNote, notes as noteOps } from '../data/notes.ts';
import { useApp } from '../app/store.ts';
import type { FromImportWorker, ImportResult, ParsedFile, ToImportWorker } from './protocol.ts';

/**
 * Import aus Obsidian und Notion. Das Zerlegen des Archivs läuft im Worker, das
 * Anlegen und Verschlüsseln hier — die Schlüssel verlassen den Hauptfaden nicht.
 */

export type ImportProgress = { done: number; total: number; step: string };

export type ImportSummary = {
  source: ImportResult['source'];
  notes: number;
  attachments: number;
  folders: number;
  skipped: number;
  failed: string[];
};

function parseArchive(file: File, onProgress: (p: ImportProgress) => void): Promise<ImportResult> {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
    const ref = crypto.randomUUID();
    worker.addEventListener('message', (event: MessageEvent<FromImportWorker>) => {
      const message = event.data;
      if (message.ref !== ref) return;
      if (message.t === 'progress') onProgress({ done: message.done, total: message.total, step: message.step });
      if (message.t === 'done') {
        worker.terminate();
        resolve(message.result);
      }
      if (message.t === 'failed') {
        worker.terminate();
        reject(new Error(message.reason));
      }
    });
    void file.arrayBuffer().then((data) => {
      worker.postMessage({ t: 'parse', ref, name: file.name, data } satisfies ToImportWorker, [data]);
    });
  });
}

/** Verweise werden erst relativ zur Notiz gesucht, dann notfalls nur über den Namen. */
function fileIndex(files: ParsedFile[]): { byPath: Map<string, ParsedFile>; byName: Map<string, ParsedFile> } {
  const byPath = new Map<string, ParsedFile>();
  const byName = new Map<string, ParsedFile>();
  for (const file of files) {
    byPath.set(file.path.replace(/^\.\//, ''), file);
    const name = file.path.split('/').pop() ?? file.path;
    if (!byName.has(name)) byName.set(name, file);
  }
  return { byPath, byName };
}

function resolve(
  reference: string,
  notePath: string,
  index: ReturnType<typeof fileIndex>,
): ParsedFile | undefined {
  const directory = notePath.split('/').slice(0, -1).join('/');
  const joined = directory ? `${directory}/${reference}` : reference;
  const normalized = joined
    .split('/')
    .reduce<string[]>((stack, part) => {
      if (part === '.' || part === '') return stack;
      if (part === '..') stack.pop();
      else stack.push(part);
      return stack;
    }, [])
    .join('/');
  return (
    index.byPath.get(normalized) ??
    index.byPath.get(reference) ??
    index.byName.get(reference.split('/').pop() ?? reference)
  );
}

export async function importArchive(
  file: File,
  onProgress: (progress: ImportProgress) => void,
): Promise<ImportSummary> {
  const result = await parseArchive(file, onProgress);
  const index = fileIndex(result.files);
  const app = useApp.getState();

  // Ordner der obersten Ebene anlegen, soweit sie noch fehlen.
  const wanted = [...new Set(result.notes.map((note) => note.folder).filter((name): name is string => !!name))];
  let createdFolders = 0;
  for (const name of wanted) {
    if (useApp.getState().folders.some((folder) => folder.name.toLowerCase() === name.toLowerCase())) {
      continue;
    }
    await app.addFolder(name);
    createdFolders += 1;
  }
  const folderId = (name: string | null): string | null =>
    name
      ? (useApp.getState().folders.find((folder) => folder.name.toLowerCase() === name.toLowerCase())?.id ??
        null)
      : null;

  const uploaded = new Map<string, { id: string; meta: Awaited<ReturnType<typeof uploadEncrypted>>['meta'] }>();
  const failed: string[] = [];
  let attachments = 0;

  for (const [position, parsed] of result.notes.entries()) {
    onProgress({ done: position + 1, total: result.notes.length, step: 'Notizen werden angelegt' });
    try {
      const note = await createNote('', folderId(parsed.folder));
      let content = parsed.content;

      for (const reference of parsed.attachments) {
        const found = resolve(reference, parsed.path, index);
        if (!found) continue;
        try {
          let entry = uploaded.get(found.path);
          if (!entry) {
            const name = found.path.split('/').pop() ?? found.path;
            entry = await uploadEncrypted(found.bytes, found.mime, name, note.id);
            uploaded.set(found.path, entry);
            attachments += 1;
          }
          await registerAttachment(note.id, entry.id, entry.meta);
          const target = attachmentMarkdown(entry.id, entry.meta);
          const escaped = reference.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          content = content.replace(
            new RegExp(`(!?)\\[([^\\]]*)\\]\\((?:${escaped}|${encodeURI(reference)})\\)`, 'g'),
            (_whole, bang: string) => (bang ? target : target.replace(/^!/, '')),
          );
        } catch (error) {
          failed.push(`${reference}: ${(error as Error).message}`);
        }
      }

      await noteOps.setContent(note.id, content);
    } catch (error) {
      failed.push(`${parsed.path}: ${(error as Error).message}`);
    }
  }

  await useApp.getState().refresh();
  return {
    source: result.source,
    notes: result.notes.length,
    attachments,
    folders: createdFolders,
    skipped: result.skipped.length,
    failed,
  };
}
